import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { 
  DxButtonModule, 
  DxTextBoxModule,
  DxTextAreaModule, 
  DxSelectBoxModule,
  DxDataGridModule,
  DxToolbarModule,
  DxCheckBoxModule
} from 'devextreme-angular';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { CrudService } from '../services/crud.service';
import { NotifyService } from '../services/notify.service';
import { PageHeaderComponent } from 'src/app/shared/page-header/page-header.component';

@Component({
  selector: 'app-support',
  standalone: true,
  imports: [CommonModule, FormsModule, DxButtonModule, DxTextBoxModule, DxTextAreaModule, DxSelectBoxModule, DxDataGridModule, DxToolbarModule, DxCheckBoxModule, PageHeaderComponent],
  templateUrl: './support.component.html',
  styleUrls: ['./support.component.css']
})
export class SupportComponent implements OnInit {
  ticketForm = {
    subject: '',
    priority: 'medium',
    description: ''
  };

  priorityOptions = [
    { value: 'Low', text: 'Low' },
    { value: 'Medium', text: 'Medium' },
    { value: 'High', text: 'High' },
    { value: 'Urgent', text: 'Urgent' }
  ];

  userDetails: any;
  isSubmitting = false;
  userTickets: any[] = [];
  ticketsLoading = false;
  showClosedTickets = true;
  searchText = '';
  searchTimeout: any = null;
  filteredData: any[] = [];

  constructor(private router: Router, private crudService: CrudService, private notifyService: NotifyService) {
    this.filteredData = [];
  }

  getStatusClass(status: string): string {
    if (!status) return '';
    
    switch (status.toLowerCase().replace(/\s+/g, '-')) {
      case 'new':
        return 'new';
      case 'in-progress':
        return 'in-progress';
      case 'escalated':
        return 'escalated';
      case 'client-to-respond':
        return 'client-to-respond';
      case 'sent-to-jira':
        return 'sent-to-jira';
      case 'on-hold':
        return 'on-hold';
      case 'closed':
        return 'closed';
      default:
        return '';
    }
  }

  getPriorityClass(priority: string): string {
    if (!priority) return '';
    
    switch (priority.toLowerCase()) {
      case 'low':
        return 'low';
      case 'medium':
        return 'medium';
      case 'high':
        return 'high';
      case 'urgent':
        return 'urgent';
      default:
        return '';
    }
  }

  refreshTickets(): void {
    this.loadUserTickets();
  }

  onSearchValueChanged(event: any): void {
    // Clear the previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // The searchText is already updated by two-way binding, 
    // but we can also get it from the event if needed
    const newValue = event.value || '';
    
    // Set a new timeout for debounced search
    this.searchTimeout = setTimeout(() => {
      console.log('Searching for:', this.searchText);
      this.applyFilters();
    }, 300); // 300ms delay
  }

  applyFilters(): void {
    let filtered = this.userTickets;
    
    // Filter out closed tickets if option is disabled
    // showClosedTickets = true means show all tickets including closed
    // showClosedTickets = false means hide closed tickets
    if (!this.showClosedTickets) {
      filtered = filtered.filter(ticket => ticket.status?.toLowerCase() !== 'closed');
    }
    
    // Apply search filter
    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.ticketNumber?.toLowerCase().includes(search) ||
        ticket.subject?.toLowerCase().includes(search) ||
        ticket.status?.toLowerCase().includes(search) ||
        ticket.priority?.toLowerCase().includes(search)
      );
    }
    
    console.log('Apply filters - showClosedTickets:', this.showClosedTickets, 'Total tickets:', this.userTickets.length, 'Filtered:', filtered.length);
    this.filteredData = filtered;
  }

  get filteredTickets(): any[] {
    return this.filteredData;
  }

  ngOnInit(): void {
    // Get user details for pre-filling
    const userDetailsData = sessionStorage.getItem("user");
    if (userDetailsData) {
      this.userDetails = JSON.parse(userDetailsData);
    }
    
    // Always try to load user's existing tickets
    this.loadUserTickets();
  }

  async loadUserTickets(): Promise<void> {
    this.ticketsLoading = true;
    
    try {
      if (!this.userDetails?.email) {
        console.log('No user email found, cannot load tickets');
        this.userTickets = [];
        return;
      }

      const apiUrl = `${environment.baseApiUrl}zoho-desk/tickets-by-email`;
      const response: any = await firstValueFrom(
        this.crudService.getData(`${apiUrl}?email=${this.userDetails.email}`)
      );
      
      // Extract tickets from the nested response structure
      this.userTickets = response.data?.tickets || [];
      console.log('Loaded tickets:', this.userTickets.length);
      
      // Apply current filters to the loaded data
      this.applyFilters();
    } catch (error) {
      console.error('Error loading user tickets:', error);
      this.userTickets = [];
    } finally {
      this.ticketsLoading = false;
    }
  }

  isFormValid(): boolean {
    return !!(this.ticketForm.subject && 
              this.ticketForm.priority && 
              this.ticketForm.description);
  }

  async submitTicket(): Promise<void> {
    if (!this.isFormValid()) {
      return;
    }

    this.isSubmitting = true;

    try {
      // Prepare the ticket data for the API
      const ticketData = {
        subject: this.ticketForm.subject,
        description: this.ticketForm.description,
        email: this.userDetails?.email || 'noreply@ontapdata.com.au', // Fallback email if user email not available
        priority: this.ticketForm.priority
      };

      // Call your API endpoint using CrudService
      const apiUrl = `${environment.baseApiUrl}zoho-desk/tickets`;
      
      const response = await firstValueFrom(
        this.crudService.insertData(apiUrl, ticketData)
      );

      // Success - show message and clear form
      this.notifyService.tellUser({
        success: true,
        data: 'Support ticket submitted successfully! You will receive a confirmation email shortly.'
      });
      this.clearForm();
      
      // Refresh the tickets list
      await this.loadUserTickets();

    } catch (error) {
      console.error('Error submitting ticket:', error);
      this.notifyService.tellUser({
        success: false,
        message: 'There was an error submitting your ticket. Please try again or contact support directly.'
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  clearForm(): void {
    this.ticketForm = {
      subject: '',
      priority: 'Medium',
      description: ''
    };
  }
}
